import moment from 'moment-timezone';
import type { PassState } from '../../../../store/redux/slices/pass.slice';
import type {
  PassEnhancedDataPayload,
  PassTemplateMeta,
} from '../../../../store/types/pass';

/** Extract all template fields as a flat array. */
export const getAllTemplateFields = (
  template: PassTemplateMeta
): { id: string; default_value: string }[] =>
  [
    template.header?.header_one,
    template.header?.header_two,
    template.header?.header_three,
    template.primary?.primary_one,
    template.primary?.primary_two,
    template.secondary?.sec_one,
    template.secondary?.sec_two,
    template.secondary?.sec_three,
    template.auxiliary?.aux_one,
    template.auxiliary?.aux_two,
    template.auxiliary?.aux_three,
    template.back_fields?.back_one,
    template.back_fields?.back_two,
    template.back_fields?.back_three,
    template.back_fields?.back_four,
    template.back_fields?.back_five,
  ].filter((f): f is { id: string; default_value: string } => !!f?.id);

/** Build the API request payload from pass state. */
export const buildPassPayload = (
  passState: PassState,
  localisationEnabled: boolean,
  passContent?: Record<string, unknown>,
  templateFields?: { id: string; default_value: string }[],
  enhancedData?: PassEnhancedDataPayload
) => {
  const expiresAt = (() => {
    if (!passState.expiryDate) return null;
    const time = passState.expiryTime || '00:00';
    const tz = passState.expiryTimezone || 'UTC';
    return moment.tz(
      `${passState.expiryDate} ${time}:00`,
      'YYYY-MM-DD HH:mm:ss',
      tz
    ).toISOString();
  })();

  const fieldPayload: Record<string, unknown> = {};

  const allFieldIds = new Set([
    ...Object.keys(passState.fieldValues),
    ...Object.keys(passState.fieldLocalisations).filter((fieldId) =>
      Object.values(passState.fieldLocalisations[fieldId] || {}).some((v) => v?.trim())
    ),
  ]);

  allFieldIds.forEach((fieldId) => {
    let baseValue = passState.fieldValues[fieldId];
    if (baseValue === undefined && passContent) {
      const raw = passContent[fieldId];
      if (raw && typeof raw === 'object' && 'value' in raw) {
        baseValue = (raw as { value: string }).value || '';
      } else if (typeof raw === 'string') {
        baseValue = raw;
      }
    }
    // Backend rejects empty value when localisation is present — use template default
    if (baseValue === undefined && templateFields) {
      const locales = passState.fieldLocalisations[fieldId];
      const hasLocaleValues = locales && Object.values(locales).some((v) => v?.trim());
      if (hasLocaleValues) {
        const tf = templateFields.find((f) => f.id === fieldId);
        if (tf?.default_value) baseValue = tf.default_value;
      }
    }
    baseValue = baseValue ?? '';

    const locales = localisationEnabled ? passState.fieldLocalisations[fieldId] : undefined;
    const hasAnyLocaleValue = locales && Object.values(locales).some((v) => v?.trim());
    if (hasAnyLocaleValue) {
      const localisationObj: Record<string, { value: string }> = {};
      Object.entries(locales).forEach(([locale, locValue]) => {
        localisationObj[locale] = { value: locValue };
      });
      fieldPayload[fieldId] = { value: baseValue, localisation: localisationObj };
    } else {
      fieldPayload[fieldId] = baseValue;
    }
  });

  return {
    pass: {
      ...fieldPayload,
      barcode: {
        enabled: passState.barcode.enabled ?? false,
        type: passState.barcode.type ?? null,
        source: passState.barcode.source ?? null,
        value: passState.barcode.value ?? '',
        displayText: passState.barcode.displayText ?? false,
      },
      nfc: passState.nfcEnabled
        ? {
            enabled: true,
            source: passState.nfcSource as 'uuid' | 'extId' | 'custom',
            customValue:
              passState.nfcSource === 'custom'
                ? passState.nfcCustomValue
                : null,
          }
        : { enabled: false, source: 'uuid' as const, customValue: null },
      expiresAt,
      ...(enhancedData ? { enhancedData } : {}),
    },
    groupTags: passState.groupTags,
  };
};

/** Build the dev mode JSON from the pass payload. */
export const buildDevModeData = (
  passState: PassState,
  localisationEnabled: boolean,
  passContent?: Record<string, unknown>,
  templateFields?: { id: string; default_value: string }[]
) => {
  const payload = buildPassPayload(passState, localisationEnabled, passContent, templateFields);
  const { barcode, nfc, expiresAt, ...payloadFields } = payload.pass;

  // Merge all pass content values with payload fields for complete dev mode view
  const allFields: Record<string, unknown> = {};
  if (passContent) {
    Object.entries(passContent).forEach(([key, val]) => {
      if (val && typeof val === 'object' && 'value' in val) {
        allFields[key] = (val as { value: string }).value || '';
      } else if (typeof val === 'string') {
        allFields[key] = val;
      }
    });
  }
  // Payload fields (edited values + localisations) take priority
  Object.assign(allFields, payloadFields);

  const pass: Record<string, unknown> = {
    ...allFields,
    ...(barcode.enabled ? { barcode } : { barcode: { enabled: false } }),
    ...(nfc.enabled
      ? {
          nfc: {
            enabled: true,
            source: nfc.source,
            ...(nfc.source === 'custom' ? { customValue: nfc.customValue } : {}),
          },
        }
      : { nfc: { enabled: false } }),
    ...(expiresAt ? { expiresAt } : {}),
  };

  const data: Record<string, unknown> = { pass };
  if (payload.groupTags.length > 0) {
    data.groupTags = payload.groupTags;
  }
  return data;
};
